import { meQueryOptions } from "@/api/auth";
import { myLocationQueryOptions } from "@/api/me";
import { PageInset } from "@/components/PageInset";
import { PageWrapper } from "@/components/ui/PageWrapper";
import { TopNavigation, TopNavigationBackButton } from "@/components/ui/TopNavigation";
import { CLAIMS_SUPPORTED_PAYOUT_COUNTRIES, toSupportedCountryCode } from "@common/utils/claims";
import { config } from "@common/utils/config";
import { Box, Field, Label, QueryHandler, Select, Text } from "@givenwell/components";
import { UpdateBeneficiaryForMemberResponse } from "@givenwell/marketplace-api";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useCanGoBack, useNavigate, useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { BeneficiaryFormElement } from "./BeneficiaryFormElement";
import { ConfirmationStep } from "./ConfirmationStep";

export const Route = createFileRoute("/_auth/_app/claims/setup")({
  component: SetupBankAccount,
});

const AIRWALLEX_ENV = config.environmentName === "prod" ? "prod" : "demo";

function SetupBankAccount() {
  const canGoBack = useCanGoBack();
  const router = useRouter();
  const navigate = useNavigate();
  const meQuery = useQuery(meQueryOptions);
  const { data: location } = useSuspenseQuery(myLocationQueryOptions);
  const [countryCode, setCountryCode] = useState<string | undefined>(toSupportedCountryCode(location?.country_code));
  // The beneficiary the save returned, held so the confirmation step can show the unmasked number
  // the member just entered — a read would give it back masked.
  const [savedBeneficiary, setSavedBeneficiary] = useState<UpdateBeneficiaryForMemberResponse>();
  const step: "form" | "confirm" = savedBeneficiary ? "confirm" : "form";

  return (
    <PageWrapper>
      <Box css={{ width: "100%", maxW: 640, mx: "auto", pb: "max(var(--safe-area-inset-bottom), 16px)" }}>
        <TopNavigation
          title="Bank Account Setup"
          left={
            <TopNavigationBackButton
              onClick={() => {
                if (step === "confirm") {
                  setSavedBeneficiary(undefined);
                } else if (canGoBack) {
                  router.history.back();
                } else {
                  navigate({
                    to: "/settings",
                    replace: true,
                  });
                }
              }}
            />
          }
        />
        <PageInset>
          <Box css={{ height: 16 }} />
          {step === "confirm" ? (
            <>
              <Text css={{ mb: 12 }}>Your bank account has been saved for claim payments.</Text>
              <ConfirmationStep
                beneficiary={savedBeneficiary!}
                onConfirm={() => navigate({ to: "/claims", replace: true })}
                onEdit={() => setSavedBeneficiary(undefined)}
              />
            </>
          ) : (
            <>
              <Text css={{ mb: 12 }}>Enter the bank account where approved claims will be paid.</Text>

              {/* We constrain the payout country to our supported list and prefill it (plus the local
                  currency) into the embedded form. AirWallex owns everything below the country: all the
                  country-specific bank fields, lookups and validation. The embedded form carries its own
                  top padding + "Bank account" heading we can't remove, so we keep our own spacing tight
                  to minimise the combined gap. */}
              {/* px:12 insets our fields to line up with the embedded AirWallex form, whose iframe has
                  12px internal padding (so its fields are 576px within the 600px column). */}
              <Field css={{ mb: 16, px: 12 }}>
                <Label>Country</Label>
                {/* Match the embedded AirWallex inputs' fill + border colour. */}
                <Select
                  value={countryCode}
                  onValueChange={setCountryCode}
                  css={{ "& select": { bg: "rgb(250, 250, 251)", borderColor: "rgb(232, 234, 237)", rounded: 6 } }}
                >
                  {CLAIMS_SUPPORTED_PAYOUT_COUNTRIES.map(country => (
                    <option key={country.code} value={country.code}>
                      {country.name}
                    </option>
                  ))}
                </Select>
              </Field>

              <QueryHandler
                query={meQuery}
                success={({ data: me }) =>
                  countryCode ? (
                    // No `key` remount: the component re-creates just the embedded element when the
                    // country changes, keeping the authorized SDK session (avoids re-minting auth codes).
                    <BeneficiaryFormElement
                      bankCountryCode={countryCode}
                      email={me?.email}
                      env={AIRWALLEX_ENV}
                      onSaved={setSavedBeneficiary}
                    />
                  ) : null
                }
              />
            </>
          )}
        </PageInset>
      </Box>
    </PageWrapper>
  );
}
